import type { LoyaltyView, PointsEntryView, PromotionView, RewardView, VoucherView } from '@khaddi/contracts';
import { Button, colors, ErrorNote, type IconName, formatDateTime, formatVnd, Grid, Icon, Screen, Spinner, Text, useAction, useApi, useAsync, useLayout, useRouter, useSession } from '@khaddi/ui';
import { useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { InlineError, Section, ShownCode } from '../components/bits';
import { MemberQr } from '../components/MemberQr';
import { useTabNav } from '../components/TabNav';
import { useFeatures } from '../state/appConfig';
import { useCart } from '../state/cart';

/** "0905 xxx 812" → "KD · 0905 xxx 812", the line printed under the member QR. */
const memberLine = (maskedPhone: string) => `KD · ${maskedPhone}`;

/** While the counter code is on screen, check this often whether the cashier has used it. */
const POLL_MS = 4000;

/** "25/10" in store time. */
const dayMonth = (iso: string) => new Date(iso).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', timeZone: 'Asia/Ho_Chi_Minh' });

const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

/** The voucher just made by a redeem: the newest pending one. */
const newestPending = (view: LoyaltyView): VoucherView | undefined =>
  view.vouchers.filter((v) => v.status === 'pending').sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];

export const LoyaltyScreen = () => {
  const nav = useTabNav('rewards');
  const { wide } = useLayout();
  const api = useApi();
  const loyalty = useAsync(() => api.loyalty.me(), [api]);
  const promotions = useAsync(() => api.catalog.activePromotions(), [api]);

  return (
    <Screen nav={nav} maxWidth={960}>
      <View style={{ backgroundColor: colors.sky, paddingTop: 16, paddingHorizontal: 20, paddingBottom: 100, minHeight: 60, justifyContent: 'center' }}>
        <Text variant="title" style={{ lineHeight: 44 }}>
          Thành viên Khao Ddi
        </Text>
      </View>
      {loyalty.loading && !loyalty.data ? <Spinner /> : null}
      {loyalty.error && !loyalty.data ? <ErrorNote message={loyalty.error} onRetry={() => void loyalty.reload()} /> : null}
      {loyalty.data ? <LoyaltyBody view={loyalty.data} onChange={loyalty.setData} /> : null}
      <View style={{ paddingHorizontal: 16, paddingTop: 18, gap: 12 }}>
        <Text variant="title" style={{ fontSize: 20 }}>
          Ưu đãi đang chạy
        </Text>
        {promotions.loading && !promotions.data ? <Spinner /> : null}
        {promotions.error ? <InlineError message={promotions.error} /> : null}
        {promotions.data && promotions.data.length === 0 ? <Text variant="caption">Hiện chưa có chương trình nào, quay lại sau nhé.</Text> : null}
        <Grid columns={wide ? 2 : 1}>
          {(promotions.data ?? []).map((p) => (
            <PromotionCard key={p.id} promotion={p} />
          ))}
        </Grid>
      </View>
    </Screen>
  );
};

const LoyaltyBody = ({ view, onChange }: { view: LoyaltyView; onChange: (view: LoyaltyView) => void }) => {
  const { loyaltyRedeem } = useFeatures();
  const { member, tiers, rules, rewards, history } = view;
  const currentIndex = tiers.findIndex((t) => t.name === member.tier.name);
  return (
    <>
      {view.notices.length > 0 ? <Notices notices={view.notices} /> : null}
      <View style={{ marginTop: view.notices.length > 0 ? 14 : -84, marginHorizontal: 16, backgroundColor: colors.surface, borderRadius: 22, padding: 16, flexDirection: 'row', gap: 16, alignItems: 'center', boxShadow: '0 6px 20px rgba(28, 69, 95,.10)' }}>
        <MemberQr code={member.memberCode} />
        <View style={{ flex: 1, gap: 6 }}>
          <View style={{ alignSelf: 'flex-start', backgroundColor: colors.gold, paddingHorizontal: 10, paddingVertical: 3, borderRadius: 999 }}>
            <Text variant="label" color={colors.navy} style={{ fontSize: 12, fontWeight: '700' }}>
              Hạng {member.tier.name}
            </Text>
          </View>
          <Text variant="display" numeric style={{ fontSize: 30, lineHeight: 32 }}>
            {member.points.toLocaleString('vi-VN')}{' '}
            <Text variant="display" style={{ fontSize: 15 }}>
              điểm
            </Text>
          </Text>
          <Text variant="caption" style={{ lineHeight: 17 }}>
            Đưa mã cho nhân viên khi mua tại quán để tích điểm
          </Text>
          <Text variant="label" color={colors.text} style={{ fontSize: 12, fontWeight: '700', letterSpacing: 0.7 }}>
            {memberLine(member.maskedPhone)}
          </Text>
          <Text variant="label" style={{ letterSpacing: 0.7 }}>
            {member.memberCode}
          </Text>
        </View>
      </View>

      <View style={{ paddingTop: 18, paddingHorizontal: 16, gap: 14 }}>
        {loyaltyRedeem ? <CounterCode view={view} onChange={onChange} /> : null}
        <Vouchers vouchers={view.vouchers} showHint={loyaltyRedeem} />
        <Section style={{ gap: 10 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Text variant="bodyStrong" style={{ fontSize: 13 }}>
              Lộ trình hạng
            </Text>
            <Text variant="caption" style={{ fontSize: 13 }}>
              {formatVnd(rules.vndPerPoint)} = 1 điểm
            </Text>
          </View>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            {tiers.map((t, i) => {
              const current = i === currentIndex;
              return (
                <View key={t.name} style={{ flex: 1, gap: 6, alignItems: 'center' }}>
                  <View style={{ alignSelf: 'stretch', height: 8, borderRadius: 4, backgroundColor: i <= currentIndex ? colors.primary : colors.border }} />
                  <Text variant="caption" color={current ? colors.text : colors.textMuted} style={{ fontWeight: current ? '800' : '500' }}>
                    {t.name}
                  </Text>
                  <Text variant="label" style={{ fontWeight: '400' }}>
                    {t.minPoints.toLocaleString('vi-VN')}
                  </Text>
                </View>
              );
            })}
          </View>
          <Text variant="caption">
            {member.nextTier
              ? `Còn ${member.pointsToNextTier.toLocaleString('vi-VN')} điểm để lên hạng ${member.nextTier.name}: ${member.nextTier.perk}.`
              : `Quyền lợi hạng ${member.tier.name}: ${member.tier.perk}.`}
          </Text>
        </Section>

        {loyaltyRedeem ? (
          <Text variant="title" style={{ fontSize: 20 }}>
            Đổi điểm lấy quà
          </Text>
        ) : null}
      </View>
      {loyaltyRedeem ? <Rewards rewards={rewards} onChange={onChange} /> : null}

      <View style={{ paddingTop: 4, paddingHorizontal: 16, gap: 12 }}>
        <Text variant="title" style={{ fontSize: 20, marginTop: 4 }}>
          Lịch sử điểm
        </Text>
        <Section style={{ paddingVertical: 0 }}>
          {history.length === 0 ? (
            <Text variant="caption" style={{ paddingVertical: 16 }}>
              Chưa có giao dịch điểm.
            </Text>
          ) : (
            history.map((h, i) => <HistoryRow key={h.id} entry={h} last={i === history.length - 1} />)
          )}
        </Section>
      </View>
    </>
  );
};

type Notice = LoyaltyView['notices'][number];

const NOTICE_STYLE: Record<Notice['kind'], { bg: string; fg: string; border: string; icon: IconName }> = {
  points_expiring: { bg: colors.goldSoft, fg: colors.goldText, border: colors.goldDeep, icon: 'clock' },
  tier_up: { bg: colors.greenSoft, fg: colors.greenText, border: colors.green, icon: 'star' },
  tier_down: { bg: colors.surface, fg: colors.textMuted, border: colors.border, icon: 'alert' },
};

/** Short server messages (points about to expire, tier changes) above the member card. */
const Notices = ({ notices }: { notices: Notice[] }) => (
  <View style={{ marginTop: -84, marginHorizontal: 16, gap: 8 }}>
    {notices.map((n, i) => {
      const tone = NOTICE_STYLE[n.kind];
      const urgent = n.kind === 'points_expiring';
      return (
        <View
          key={`${n.kind}-${i}`}
          role={urgent ? 'alert' : 'status'}
          aria-live={urgent ? 'assertive' : 'polite'}
          accessibilityLiveRegion={urgent ? 'assertive' : 'polite'}
          style={{ backgroundColor: tone.bg, borderRadius: 18, borderWidth: 1, borderColor: tone.border, paddingHorizontal: 14, paddingVertical: 12, minHeight: 48, flexDirection: 'row', alignItems: 'center', gap: 10 }}
        >
          <Icon name={tone.icon} size={20} color={tone.fg} filled={n.kind === 'tier_up'} />
          <Text variant="body" color={tone.fg} style={{ flex: 1, fontWeight: '600', lineHeight: 20 }}>
            {n.text}
          </Text>
        </View>
      );
    })}
  </View>
);

/** Kinds that get a label next to the date: gifts read like earning, expiry like spending. */
const KIND_LABEL: Partial<Record<PointsEntryView['kind'], string>> = {
  gift: 'Quà tặng',
  birthday: 'Quà sinh nhật',
  expire: 'Hết hạn',
};

const HistoryRow = ({ entry, last }: { entry: PointsEntryView; last: boolean }) => {
  const label = KIND_LABEL[entry.kind];
  const positive = entry.kind === 'expire' ? false : entry.points >= 0;
  const amount = Math.abs(entry.points).toLocaleString('vi-VN');
  return (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 12, minHeight: 56, paddingVertical: 8, borderBottomWidth: last ? 0 : 1, borderBottomColor: colors.divider }}>
      <View style={{ flex: 1 }}>
        <Text style={{ fontWeight: '600' }}>{entry.description}</Text>
        <Text variant="caption">
          {label ? (
            <Text variant="caption" color={positive ? colors.greenText : colors.danger} style={{ fontWeight: '700' }}>
              {label} ·{' '}
            </Text>
          ) : null}
          {formatDateTime(entry.createdAt)}
        </Text>
      </View>
      <Text variant="bodyStrong" numeric color={positive ? colors.green : colors.danger}>
        {positive ? `+${amount}` : `−${amount}`}
      </Text>
    </View>
  );
};

/** Horizontal reward cards; redeeming spends points, so the first tap asks for confirmation. */
const Rewards = ({ rewards, onChange }: { rewards: RewardView[]; onChange: (view: LoyaltyView) => void }) => {
  const api = useApi();
  const { user } = useSession();
  const { navigate } = useRouter();
  const verified = user?.phoneVerified ?? false;
  const [confirming, setConfirming] = useState<string | null>(null);
  const [redeemed, setRedeemed] = useState<{ name: string; code: string | null } | null>(null);
  const redeem = useAction(async (reward: RewardView) => {
    const view = await api.loyalty.redeem(reward.id);
    onChange(view);
    setConfirming(null);
    setRedeemed({ name: reward.name, code: newestPending(view)?.code ?? null });
  });

  return (
    <View style={{ gap: 8, paddingTop: 14, paddingBottom: 14 }}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 10, paddingHorizontal: 16 }}>
        {rewards.map((r) => {
          const asking = confirming === r.id;
          const enabled = verified && r.affordable && !redeem.pending;
          return (
            <View key={r.id} style={{ width: 150, backgroundColor: colors.surface, borderRadius: 18, padding: 12, gap: 8 }}>
              <View style={{ height: 64, borderRadius: 12, backgroundColor: r.background, alignItems: 'center', justifyContent: 'center' }}>
                <Text variant="title">{r.badge}</Text>
              </View>
              <Text variant="bodyStrong" style={{ fontSize: 13, lineHeight: 17, minHeight: 34 }}>
                {r.name}
              </Text>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={r.affordable ? `Đổi ${r.name} với ${r.costPoints} điểm` : `${r.name}: chưa đủ ${r.costPoints} điểm`}
                accessibilityState={{ disabled: !enabled }}
                disabled={!enabled}
                onPress={() => (asking ? void redeem.run(r) : setConfirming(r.id))}
                style={{ height: 36, borderRadius: 18, backgroundColor: !r.affordable ? colors.divider : asking ? colors.navy : colors.primary, alignItems: 'center', justifyContent: 'center' }}
                hitSlop={4}
              >
                <Text variant="label" color={!r.affordable ? colors.textMuted : asking ? colors.textOnDark : colors.onPrimary} style={{ fontSize: 12, fontWeight: '700' }}>
                  {asking ? (redeem.pending ? 'Đang đổi…' : 'Xác nhận đổi?') : `${r.costPoints.toLocaleString('vi-VN')} điểm`}
                </Text>
              </Pressable>
            </View>
          );
        })}
      </ScrollView>
      <View style={{ paddingHorizontal: 16 }}>
        {!verified ? (
          <Pressable accessibilityRole="link" onPress={() => navigate('/verify')} style={{ minHeight: 44, justifyContent: 'center' }}>
            <Text variant="caption" color={colors.primaryText} style={{ fontWeight: '700' }}>
              Xác minh số điện thoại tại quầy để đổi quà →
            </Text>
          </Pressable>
        ) : null}
        <InlineError message={redeem.error} />
        {redeemed ? (
          <Text variant="caption" color={colors.greenText} style={{ fontWeight: '600' }}>
            {redeemed.code
              ? `Đã đổi “${redeemed.name}” · phiếu ${redeemed.code}. Đưa phiếu cho thu ngân để nhận quà (hạn 30 ngày).`
              : `Đã đổi “${redeemed.name}”. Đưa phiếu cho thu ngân để nhận quà (hạn 30 ngày).`}
          </Text>
        ) : null}
      </View>
    </View>
  );
};

type Shown = { code: string; until: number };

/**
 * "Dùng điểm tại quầy": a single-use code the cashier types into the console to take points off the
 * iPOS bill and/or hand over vouchers. While it's up we poll the loyalty view to tell the customer
 * when it went through.
 */
const CounterCode = ({ view, onChange }: { view: LoyaltyView; onChange: (view: LoyaltyView) => void }) => {
  const api = useApi();
  const { user } = useSession();
  const { navigate } = useRouter();
  const verified = user?.phoneVerified ?? false;
  const [shown, setShown] = useState<Shown | null>(null);
  const [now, setNow] = useState(Date.now());
  const [used, setUsed] = useState<string[]>([]);
  /** What the view looked like when the code went up; the poll compares against it. */
  const baseline = useRef<LoyaltyView>(view);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // A redeem in the carousel (or any other refresh) moves the baseline too, so it isn't mistaken for the cashier.
  useEffect(() => {
    baseline.current = view;
  }, [view]);

  const fetchCode = useAction(async () => {
    const result = await api.loyalty.counterCode();
    setUsed([]);
    setNow(Date.now());
    setShown({ code: result.code, until: Date.now() + result.expiresInSeconds * 1000 });
  });

  const secondsLeft = shown ? Math.max(0, Math.round((shown.until - now) / 1000)) : 0;
  const done = used.length > 0;
  const counting = shown !== null && secondsLeft > 0 && !done;

  useEffect(() => {
    if (!counting) return;
    let alive = true;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(() => {
      void api.loyalty
        .me()
        .then((next) => {
          if (!alive) return;
          const before = baseline.current;
          const lines: string[] = [];
          const spent = before.member.points - next.member.points;
          if (spent > 0) lines.push(`Đã dùng ${spent.toLocaleString('vi-VN')} điểm`);
          const wasPending = new Set(before.vouchers.filter((v) => v.status === 'pending').map((v) => v.id));
          for (const v of next.vouchers) if (v.status === 'claimed' && wasPending.has(v.id)) lines.push(`Đã nhận ${v.rewardName}`);
          if (lines.length === 0) return;
          baseline.current = next;
          onChangeRef.current(next);
          setUsed(lines);
        })
        .catch(() => {});
    }, POLL_MS);
    return () => {
      alive = false;
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [counting, api]);

  if (!verified) {
    return (
      <Pressable
        accessibilityRole="link"
        accessibilityLabel="Dùng điểm tại quầy: cần xác minh số điện thoại trước"
        onPress={() => navigate('/verify')}
        style={{ minHeight: 64, borderRadius: 18, backgroundColor: colors.surface, padding: 14, flexDirection: 'row', alignItems: 'center', gap: 12 }}
      >
        <CounterIcon />
        <View style={{ flex: 1, gap: 2 }}>
          <Text variant="bodyStrong">Dùng điểm tại quầy</Text>
          <Text variant="caption" color={colors.primaryText} style={{ fontWeight: '700' }}>
            Xác minh số điện thoại tại quầy để dùng điểm →
          </Text>
        </View>
      </Pressable>
    );
  }

  if (!shown) {
    return (
      <View style={{ gap: 6 }}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Dùng điểm tại quầy: hiện mã cho thu ngân"
          accessibilityState={{ busy: fetchCode.pending }}
          disabled={fetchCode.pending}
          onPress={() => void fetchCode.run()}
          style={({ pressed }) => ({
            minHeight: 64,
            borderRadius: 18,
            backgroundColor: colors.navy,
            padding: 14,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 12,
            transform: [{ scale: pressed ? 0.98 : 1 }],
          })}
        >
          <CounterIcon dark />
          <View style={{ flex: 1, gap: 2 }}>
            <Text variant="bodyStrong" color={colors.textOnDark} style={{ fontSize: 16 }}>
              {fetchCode.pending ? 'Đang lấy mã…' : 'Dùng điểm tại quầy'}
            </Text>
            <Text variant="caption" color={colors.textOnDarkMuted}>
              Trừ điểm vào bill hoặc nhận phiếu quà
            </Text>
          </View>
          <Icon name="chevronRight" size={20} color={colors.textOnDark} />
        </Pressable>
        <InlineError message={fetchCode.error} />
      </View>
    );
  }

  const expired = secondsLeft === 0 && !done;
  return (
    <Section style={{ gap: 12, borderWidth: 2, borderColor: colors.gold }}>
      <Text variant="bodyStrong" style={{ fontSize: 16 }}>
        Mã dùng điểm tại quầy
      </Text>
      <ShownCode code={shown.code} secondsLeft={secondsLeft} size="lg" dimmed={done || expired} note={done ? 'Mã đã được dùng' : expired ? 'Mã đã hết hạn' : undefined} />
      {done ? (
        <View accessibilityLiveRegion="polite" style={{ backgroundColor: colors.greenSoft, borderRadius: 12, padding: 12, gap: 4 }}>
          {used.map((line) => (
            <View key={line} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Icon name="checkCircle" size={18} color={colors.green} />
              <Text variant="bodyStrong" color={colors.greenText}>
                {line}
              </Text>
            </View>
          ))}
        </View>
      ) : (
        <Text variant="caption" style={{ textAlign: 'center', lineHeight: 18 }}>
          Đưa mã này cho thu ngân để dùng điểm hoặc nhận quà. Thu ngân nhập giảm giá trên bill.
        </Text>
      )}
      <InlineError message={fetchCode.error} />
      <View style={{ flexDirection: 'row', gap: 10 }}>
        <Button label="Lấy mã mới" variant="outline" size="md" style={{ flex: 1 }} onPress={() => void fetchCode.run()} loading={fetchCode.pending} />
        <Button label="Xong" variant={done || expired ? 'primary' : 'secondary'} size="md" style={{ flex: 1 }} onPress={() => setShown(null)} />
      </View>
    </Section>
  );
};

const CounterIcon = ({ dark = false }: { dark?: boolean }) => (
  <View style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: dark ? 'rgba(255,255,255,.18)' : colors.goldSoft, alignItems: 'center', justifyContent: 'center' }}>
    <Icon name="ticket" size={22} color={dark ? colors.gold : colors.goldText} />
  </View>
);

/** Rewards redeemed in the app; pending ones are what the customer still has to collect at the counter. */
const Vouchers = ({ vouchers, showHint }: { vouchers: VoucherView[]; showHint: boolean }) => {
  if (vouchers.length === 0) return null;
  return (
    <View style={{ gap: 10 }}>
      <Text variant="title" style={{ fontSize: 20 }}>
        Phiếu quà của bạn
      </Text>
      {vouchers.map((v) => (v.status === 'pending' ? <PendingVoucher key={v.id} voucher={v} showHint={showHint} /> : <PastVoucher key={v.id} voucher={v} />))}
    </View>
  );
};

const PendingVoucher = ({ voucher, showHint }: { voucher: VoucherView; showHint: boolean }) => (
  <View
    accessible
    accessibilityLabel={`Phiếu quà ${voucher.rewardName}, mã ${voucher.code}, hết hạn ${dayMonth(voucher.expiresAt)}`}
    style={{ backgroundColor: colors.goldSoft, borderRadius: 18, borderWidth: 1.5, borderStyle: 'dashed', borderColor: colors.goldDeep, padding: 14, gap: 6 }}
  >
    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <Text variant="bodyStrong" color={colors.goldText} style={{ fontFamily: MONO, fontSize: 22, letterSpacing: 1.5 }}>
        {voucher.code}
      </Text>
      <Text variant="label" numeric color={colors.goldText} style={{ fontSize: 12, fontWeight: '700' }}>
        Hết hạn {dayMonth(voucher.expiresAt)}
      </Text>
    </View>
    <Text variant="bodyStrong">{voucher.rewardName}</Text>
    {showHint ? <Text variant="caption">Đưa mã cho thu ngân kèm mã dùng điểm</Text> : null}
  </View>
);

const PastVoucher = ({ voucher }: { voucher: VoucherView }) => {
  const status =
    voucher.status === 'claimed'
      ? `Đã nhận ${dayMonth(voucher.claimedAt ?? voucher.createdAt)}`
      : `Hết hạn · đã hoàn ${voucher.points.toLocaleString('vi-VN')} điểm`;
  return (
    <View
      accessible
      accessibilityLabel={`Phiếu ${voucher.code}, ${voucher.rewardName}: ${status}`}
      style={{ backgroundColor: colors.surface, borderRadius: 18, paddingHorizontal: 14, paddingVertical: 10, minHeight: 56, flexDirection: 'row', alignItems: 'center', gap: 12, opacity: 0.75 }}
    >
      <View style={{ flex: 1 }}>
        <Text variant="body" color={colors.textMuted} style={{ fontWeight: '600' }}>
          {voucher.rewardName}
        </Text>
        <Text variant="label" color={colors.textSubtle} style={{ fontFamily: MONO, fontWeight: '500' }}>
          {voucher.code}
        </Text>
      </View>
      <Text variant="caption" numeric color={colors.textMuted}>
        {status}
      </Text>
    </View>
  );
};

const PromotionCard = ({ promotion }: { promotion: PromotionView }) => {
  const { navigate } = useRouter();
  const { setVoucherCode } = useCart();
  const { onlineOrdering } = useFeatures();
  const code = promotion.code;
  return (
    <Section style={{ flexDirection: 'row', gap: 12, alignItems: 'center' }}>
      <View style={{ width: 56, height: 56, borderRadius: 14, backgroundColor: promotion.badgeColor, alignItems: 'center', justifyContent: 'center' }}>
        <Text variant="title" style={{ fontSize: 18 }}>
          {promotion.badge || '%'}
        </Text>
      </View>
      <View style={{ flex: 1, gap: 2 }}>
        <Text variant="bodyStrong">{promotion.name}</Text>
        {promotion.condition ? <Text variant="caption">{promotion.condition}</Text> : null}
        <Text variant="label" style={{ fontWeight: '400' }}>
          {promotion.typeLabel} · {promotion.whenLabel}
        </Text>
      </View>
      {code && !onlineOrdering ? (
        <View style={{ paddingHorizontal: 12, paddingVertical: 6, borderRadius: 22, borderWidth: 1.5, borderStyle: 'dashed', borderColor: colors.primaryText, alignItems: 'center' }}>
          <Text variant="label" color={colors.primaryText} style={{ fontSize: 12, fontWeight: '800' }}>
            {code}
          </Text>
          <Text variant="label" color={colors.primaryText} style={{ fontSize: 10 }}>
            Đọc mã tại quầy
          </Text>
        </View>
      ) : code ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Dùng mã ${code} cho đơn tiếp theo`}
          onPress={() => {
            setVoucherCode(code);
            navigate('/checkout');
          }}
          style={{ minHeight: 44, paddingHorizontal: 12, borderRadius: 22, borderWidth: 1.5, borderStyle: 'dashed', borderColor: colors.primaryText, alignItems: 'center', justifyContent: 'center' }}
        >
          <Text variant="label" color={colors.primaryText} style={{ fontSize: 12, fontWeight: '800' }}>
            {code}
          </Text>
          <Text variant="label" color={colors.primaryText} style={{ fontSize: 10 }}>
            Dùng mã
          </Text>
        </Pressable>
      ) : null}
    </Section>
  );
};
